import { MenuItem } from 'primereact/menuitem';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { AllowAccess } from '@features/auth/ControlAccess/AllowAccess/AllowAccess';
import ExportOneFlightModal from '@features/campaigns/details/components/Modals/ExportOneFlightModal/ExportOneFlightModal';
import ExportButton from '@features/campaigns/details/shared/components/ExportButton/ExportButton.tsx';
import { ExportMenu } from '@features/campaigns/details/shared/components/ExportMenu/ExportMenu.tsx';
import commonStyles from '@features/campaigns/shared/components/ExportMenu/ExportMenu.module.scss';
import ExportMenuItem from '@features/campaigns/shared/components/ExportMenu/ExportMenuItem/ExportMenuItem.tsx';
import useCampaignDetails from '@features/campaigns/shared/hooks/useCampaignDetails.ts';
import { CampaignTypes } from '@features/campaigns/shared/types/campaign.ts';
import {
  DownloadedFileExtension,
  DownloadedFileTypes,
} from '@features/campaigns/shared/types/exportFileDownloadType.ts';
import { i18nNameSpace } from '@shared/consts/i18n';
import { Permissions } from '@shared/consts/permissions';
import { useExportFileDownload } from '@shared/hooks/useExportFileDownload.ts';

export default function ExportDetails() {
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);
  const menu = useRef(null);
  const { campaignId } = useParams();
  const { data } = useCampaignDetails({
    campaignId: campaignId!,
  });
  const campaignType = data?.type;
  const isExternalCampaign = campaignType === CampaignTypes.SELF_SERVICE;
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const { setDownloadFileExtension, isLoading } = useExportFileDownload({
    fileType: DownloadedFileTypes.DETAILS,
  });

  const handleToggleModalVisibility = () => setIsPreviewModalVisible((prevState) => !prevState);

  const handleOnMenuItemClick = (fileExtension: DownloadedFileExtension) => {
    setDownloadFileExtension(fileExtension);
  };

  const items: MenuItem[] = [
    {
      items: [
        {
          label: t('page.campaignDetails.campaignDetailsTab.exportOptions.pptx'),
          className: commonStyles.menuItem,
          data: {
            fileExtension: 'pptx',
          },
          template: (item) => (
            <AllowAccess
              requiredPermissions={[
                Permissions.CAMPAIGN_EXPORT_ALL,
                Permissions.CAMPAIGN_EXPORT_OWNED,
                Permissions.CAMPAIGN_EXPORT_ASSIGNED_ADVERTISERS,
              ]}
            >
              <ExportMenuItem
                item={item}
                handleOnClick={() => handleOnMenuItemClick(item.data.fileExtension)}
                menuReference={menu}
                icon='pi-file-o'
              />
            </AllowAccess>
          ),
        },
      ],
    },
  ];
  if (!isExternalCampaign) {
    items.push({
      label: t('page.campaignDetails.campaignDetailsTab.exportOptions.oneFlight'),
      className: commonStyles.menuItem,
      template: (item) => (
        <AllowAccess
          requiredPermissions={[
            Permissions.CAMPAIGN_EXPORT_ALL,
            Permissions.CAMPAIGN_EXPORT_OWNED,
            Permissions.CAMPAIGN_EXPORT_ASSIGNED_ADVERTISERS,
          ]}
        >
          <ExportMenuItem
            item={item}
            handleOnClick={() => handleToggleModalVisibility()}
            menuReference={menu}
            icon='pi-cloud-upload'
          />
        </AllowAccess>
      ),
    });
  }

  return (
    <AllowAccess
      requiredPermissions={[
        Permissions.CAMPAIGN_EXPORT_ALL,
        Permissions.CAMPAIGN_EXPORT_OWNED,
        Permissions.CAMPAIGN_EXPORT_ASSIGNED_ADVERTISERS,
      ]}
    >
      <ExportButton
        menuReference={menu}
        isLoading={isLoading}
        label='page.campaignDetails.campaignDetailsTab.labels.exportDetails'
        dataTestId='campaign-details-export-button'
      />
      {!isExternalCampaign && (
        <ExportOneFlightModal
          isModalVisible={isPreviewModalVisible}
          toggleModalVisibility={handleToggleModalVisibility}
          headerText={t('page.campaignDetails.campaignDetailsTab.modals.headers.oneFlightClientBrand')}
        />
      )}
      <ExportMenu menuItems={items} menuRef={menu} dataTestId='export-details-menu' />
    </AllowAccess>
  );
}
